import { useCallback } from "react";

import DocumentationButton from "components/DocumentationButton";
import {
  CloudArrows,
  EmptystateDataFlowColored,
  EmptystateSearchNoResultsColored,
  Pickaxe,
} from "components/icons/generated";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import useAnalytics from "hooks/useAnalytics";
import useCanCreateCloudIntegration from "shared/CloudIntegration/access/useCanCreateCloudIntegration";
import { SCAN_INTEGRATION_FORM_MESSAGE } from "shared/Scan/constants";
import useCanCreateWorkerPool from "shared/WorkerPool/access/useCanCreateWorkerPool";
import { getDocsUrl } from "utils/getDocsUrl";
import { showAwsIntegrationFormDrawer } from "views/Account/Integrations/registry/AWS/components/FormDrawer";
import { showWorkerPoolFormDrawer } from "views/Account/WorkerPools/CreateWorkerPool";

import ScheduleScanButton from "../ScheduleScanButton";
import RequirementCard from "./RequirementCard";

type ScansEmptyStateProps = {
  hasNoResults?: boolean;
  isScheduleScanDisabled?: boolean;
  scheduleScanTooltipMessage?: string;
  hasNoAttachableIntegration?: boolean;
  hasNoAttachableWorkerPool?: boolean;
  onRequirementCreated?: () => void;
};

const ScansEmptyState = ({
  hasNoResults,
  isScheduleScanDisabled,
  scheduleScanTooltipMessage,
  hasNoAttachableIntegration,
  hasNoAttachableWorkerPool,
  onRequirementCreated,
}: ScansEmptyStateProps) => {
  const trackAnalytics = useAnalytics();
  const canCreateCloudIntegration = useCanCreateCloudIntegration();
  const canCreateWorkerPool = useCanCreateWorkerPool();

  const handleSetUpIntegration = useCallback(() => {
    showAwsIntegrationFormDrawer({
      trackAnalytics,
      onSuccess: onRequirementCreated,
      formMessage: SCAN_INTEGRATION_FORM_MESSAGE,
    });
  }, [trackAnalytics, onRequirementCreated]);

  const handleCreateWorkerPool = useCallback(() => {
    showWorkerPoolFormDrawer({ onSuccess: onRequirementCreated });
  }, [onRequirementCreated]);

  if (hasNoResults) {
    return (
      <EmptyState
        title="No results"
        caption="Don't give up! Check the spelling or try something less specific."
        icon={EmptystateSearchNoResultsColored}
        fullHeight
        announce
      />
    );
  }

  if (hasNoAttachableIntegration || hasNoAttachableWorkerPool) {
    return (
      <EmptyState
        title="No scans scheduled"
        caption="Scan your cloud accounts to find resources Spacelift doesn't manage yet. Before you schedule your first scan, set up the requirements below."
        icon={EmptystateDataFlowColored}
        fullHeight
        announce
      >
        <Box direction="column" gap="medium">
          {hasNoAttachableIntegration && (
            <RequirementCard
              icon={CloudArrows}
              iconColor="blue"
              title="Cloud integration"
              description="Provides credentials to allow Spacelift to scan your cloud resources."
              actionLabel="Set up"
              isActionDisabled={!canCreateCloudIntegration.result}
              actionTooltip={canCreateCloudIntegration.message}
              onAction={handleSetUpIntegration}
            />
          )}
          {hasNoAttachableWorkerPool && (
            <RequirementCard
              icon={Pickaxe}
              iconColor="yellow"
              title="Private worker pool"
              description="Runs the scan and reaches resources in your private network."
              actionLabel="Create"
              isActionDisabled={!canCreateWorkerPool.result}
              actionTooltip={canCreateWorkerPool.message}
              onAction={handleCreateWorkerPool}
            />
          )}
        </Box>
        <Box gap="large">
          <ScheduleScanButton
            disabled={isScheduleScanDisabled}
            tooltipMessage={scheduleScanTooltipMessage}
          />
          <DocumentationButton
            to={getDocsUrl("/concepts/resources#account-level-resources")}
            label="Documentation"
          />
        </Box>
      </EmptyState>
    );
  }

  return (
    <EmptyState
      title="No scans scheduled"
      caption="Configure infrastructure scanning to detect resources outside of Spacelift's management and start bringing them under control."
      icon={EmptystateDataFlowColored}
      fullHeight
      announce
    >
      <Box gap="large">
        <ScheduleScanButton
          disabled={isScheduleScanDisabled}
          tooltipMessage={scheduleScanTooltipMessage}
        />
        <DocumentationButton
          to={getDocsUrl("/concepts/resources#account-level-resources")}
          label="Documentation"
        />
      </Box>
    </EmptyState>
  );
};

export default ScansEmptyState;
